import React from "react";
import { styled } from "../stitches.config";
import {
  AnyBlock,
  BlockType,
  UndefinedBlock as UndefinedBlockType,
} from "../types";

import Icon from "../components/Icon";
import { Button } from "../components/Button";
import { blockYouJustCreatedAtom } from "../jotai/atoms";
import { useUpdateAtom } from "jotai/utils";
import { useFeatureFlags, useUserInfo } from "../roomservice/maps";
import { defaultCSS } from "./CssBlock";
import { brown } from "../stitches.config";
import { defaultSearch } from "./ImageSearchBlock";

const BLOCK_TYPE_ICONS = {
  face: "FaceDock",
  web: "WebBlock",
  text: "TextBlock",
  link: "Link", // not used
  image: "ImgBlock",
  imageSearch: "Search",
  css: "CssBlock",
};

const BLOCK_TYPE_LABELS: Record<string, string> = {
  face: "Face Dock",
  web: "Browse Web",
  text: "Write Text",
  link: "Link", // not used
  image: "Upload Image",
  imageSearch: "Search for Images",
  css: "Write Style Rules",
};

function BlockTypeButton({
  type,
  label,
  hover,
  iconOnly,
  onClick,
  onHover,
}: {
  type: BlockType;
  label: string | undefined;
  hover: boolean;
  iconOnly: boolean;
  onClick: (type: BlockType) => void;
  onHover: (type: BlockType) => void;
}) {
  return (
    <SelectTypeButton
      key={type}
      style={{ margin: 0, borderRadius: 0 }}
      type="button"
      value={type}
      onMouseDown={(e: any) => {
        e.stopPropagation();
      }}
      onClick={() => onClick(type)}
      onMouseOver={() => onHover(type)}
      title={label}
      aria-label={label}
      // display artificial hover state
      hover={hover}
    >
      <ButtonLabel iconOnly={iconOnly}>
        <ButtonLabelIcon>
          <Icon color={brown} icon={BLOCK_TYPE_ICONS[type!] as any} />{" "}
        </ButtonLabelIcon>

        {!iconOnly && <ButtonLabelText>{label}</ButtonLabelText>}
      </ButtonLabel>
    </SelectTypeButton>
  );
}

function UndefinedBlock({
  block,
  updateBlock,
}: {
  block: UndefinedBlockType;
  updateBlock: (block: Partial<AnyBlock>) => void;
}) {
  let setBlockYouJustCreated = useUpdateAtom(blockYouJustCreatedAtom);

  let [user] = useUserInfo(block?.madeBy);

  function onClick(type: BlockType) {
    const newBlock: Partial<AnyBlock> = {
      type,
    };

    switch (newBlock.type) {
      case "text":
        newBlock.value = "";
        break;
      case "web":
        newBlock.value = "https://jspaint.app/";
        break;
      case "css":
        newBlock.value = !!user ? defaultCSS(user) : "";
        break;
      case "imageSearch":
        newBlock.value = "[]";
        newBlock.text = defaultSearch();
        newBlock.searchType = "transparent";
        break;
      default:
        newBlock.value = undefined;
    }
    setBlockYouJustCreated(block.id);
    updateBlock(newBlock);
  }
  function onHover(type: BlockType) {
    // used to display "artificial" hover state seen by other users
    updateBlock({
      value: type,
    });
  }
  let [featureFlags] = useFeatureFlags();

  let blockTypes = ["text", "face", "image", "imageSearch", "css", "web"];
  if (!featureFlags?.webBlock) {
    blockTypes = blockTypes.filter((t) => t !== "web");
  }
  const num_rows = Math.min(
    Math.max(Math.floor(block.size.height / 2), 1),
    blockTypes.length
  );
  const iconOnly = block.size.width < 6;
  return (
    <ButtonWrapper
      style={{ gridTemplateRows: `repeat(${num_rows}, 1fr)` }}
      onMouseLeave={() => onHover(undefined)}
    >
      {blockTypes.slice(0, num_rows).map((type) => (
        <BlockTypeButton
          key={type}
          type={type as BlockType}
          label={BLOCK_TYPE_LABELS[type!]}
          hover={block.value === type}
          iconOnly={iconOnly}
          onClick={onClick}
          onHover={onHover}
        />
      ))}
    </ButtonWrapper>
  );
}

export default UndefinedBlock;

const ButtonWrapper = styled("ul", {
  display: "grid",
  gridAutoFlow: "column",
  gridGap: "2px",
  margin: "2px",
  overflow: "hidden",
});

const SelectTypeButton = styled(Button, {
  borderRadius: "0",
  width: "100%",
  height: "100%",
  display: "grid",
  gridTemplateColumns: "1fr",
  gridAutoFlow: "column",
});

const ButtonLabel = styled("div", {
  borderRadius: "0",
  display: "grid",
  width: "100%",
  height: "100%",
  variants: {
    iconOnly: {
      true: {
        gridTemplateColumns: "1fr",
      },
      false: {
        gridTemplateColumns: "64px 1fr",
      },
    },
  },
});

const ButtonLabelText = styled("span", {
  textAlign: "left",
  display: "inline-flex",
  alignItems: "center",
  color: "$brown",
});

const ButtonLabelIcon = styled(ButtonLabelText, {
  justifyContent: "center",
});
